/**
 * Production-ready reimagined Contribution History component.
 *
 * Renders unconditionally inside the `slr.dashboard` route — this is the
 * production contribution history.
 *
 * Strips out: demo mode, DataIndicator badges, "Back to Finance" link,
 * links to reimagined-only routes (optimizer, etc).
 */
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import {
  getEmployeePlanAccountEligibilityCriteriaRequest,
  unwrapEmployeePlanAccountEligibilityCriteria,
  type EmployeePlanAccountEligibilityCriteriaResponse,
} from 'tio-data/requests/employee-plan-account-eligibility-criteria';
import type { Enum as EnumModel } from 'tio-data/data/resources/enum.type';
import { humanizeEnumDot } from 'tio-common/utils/enum-format';
import { escapeHtml } from 'tio-common/utils/escape-html';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import { or } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-ui/components/layout';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import ReSelect from 'tio-ui/components/select';
import Checkbox from 'tio-ui/components/checkbox';
import Chip from 'tio-ui/components/chip';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import ContributionHero from 'tio-employee/components/slr/contribution-hero';
import { suppressVerdictOwnedActionItems } from 'tio-employee/utils/slr-loan-standing-verification';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';
import AccountSelectorDrawer from 'tio-employee/components/slr/account-selector-drawer';
import PdfExportDrawer from 'tio-employee/components/slr/pdf-export-drawer';
import BenefitDetailDrawer from 'tio-employee/components/slr/benefit-detail-drawer';
import PaymentHistoryDrawer from 'tio-employee/components/slr/payment-history-drawer';
import type { SmartAction } from 'tio-employee/utils/slr/smart-action';
import { LinkTo } from '@ember/routing';
import type {
  ContributionHistoryData,
  ContributionPaymentRecord,
  LiveSection127Info,
  SlrPageState,
} from 'tio-employee/services/reimagined-data';
import { getPaymentYear } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type EmployeeService from 'tio-employee/services/employee';
import type { EmployerContributions } from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type IntlService from 'ember-intl/services/intl';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PostalAddressExtension as PostalAddressModel } from 'tio-data/data/resources/postal-address.ext';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import {
  buildDebtMilestones,
  computeProgressPercent,
} from 'tio-employee/utils/slr/debt-milestones';
import { formatBenefitYearRange } from 'tio-employee/utils/benefit-year';
import { effectiveLimitCents } from 'tio-employee/utils/contribution-limits';
import {
  PhArrowCounterClockwise,
  PhBank,
  PhCheckCircle,
  PhDownloadSimple,
  PhHandshake,
  PhMoneyWavy,
  PhPercent,
  PhProhibit,
  PhWarningCircle,
  PhLinkBreak,
  PhShieldSlash,
} from 'ember-phosphor-icons';
import DocumentUploadDrawer from 'tio-employee/components/slr/document-upload-drawer';
import IssueNotificationCard from 'tio-employee/components/slr/issue-notification-card';
import AddressSelectionCard from 'tio-employee/components/slr/address-selection-card';
import GroupedIssueNotificationCard from 'tio-employee/components/slr/grouped-issue-notification-card';
import PageStateUploadCta from 'tio-employee/components/slr/page-state-upload-cta';
import ResolutionAlert from 'tio-employee/components/slr/resolution-alert';
import {
  actionItemBlocksContribution,
  getActionItemIconBg,
  getActionItemIconColor,
  type ContributionActionItem,
} from 'tio-employee/services/reimagined-data';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';

/**
 * Parse a date string, treating date-only values (YYYY-MM-DD) as LOCAL dates —
 * `new Date('2026-12-01')` is UTC midnight, which renders the previous
 * day/month in western timezones. Returns null for unparseable input.
 */
function parseLocalishDate(value: string | null | undefined) {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export interface ReimaginedContributionHistoryModel {
  contributionHistory: ContributionHistoryData;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  person: PersonModel;
  section127Info: LiveSection127Info;
  hasStrategyFinderResults: boolean;
  limitsStatus?: LimitsStatusDataSignature;
}

interface ReimaginedContributionHistorySignature {
  Args: {
    model: ReimaginedContributionHistoryModel;
    /**
     * Loan-standing verification state for the contribution-receiving account
     * (already scoped upstream — never other accounts'). Drives the verified
     * chip on the receiving-account card and the hero's requires-verification
     * treatment. Null/absent when the feature is off or no state exists.
     */
    loanStandingState?: SlrLoanStandingAuditState | null;
    /**
     * How many attention items the route renders into the `:attention` block.
     * Included in the single "Needs Your Attention" header count so slotted
     * cards and server action items read as one section.
     */
    slotAttentionCount?: number;
    /** Renders the LRAP "Enrolled" status chip in the hero. */
    lrapEnrolled?: boolean;
  };
  Blocks: {
    /**
     * Attention cards owned by the route (e.g. loan-standing verification),
     * rendered INSIDE the unified "Needs Your Attention" section ahead of the
     * server action items. Cards only: the section supplies the header. Pair
     * with @slotAttentionCount so the header count is right.
     */
    attention: [];
    /**
     * A route-owned promotional or enrollment tile, rendered below the
     * attention zone — the position the TA dashboard gives its LPN blurb. The
     * block owns its own conditional so nothing renders an empty wrapper.
     */
    promo: [];
  };
}

const createProductionActions = (options: {
  section127Remaining: number;
  section127Used: number;
  section127AnnualLimit: number;
  hasStrategyFinderResults: boolean;
  formatCurrency: (amount: number) => string;
}): SmartAction[] => {
  const {
    section127Remaining,
    section127Used,
    section127AnnualLimit,
    hasStrategyFinderResults,
    formatCurrency,
  } = options;
  const actions: SmartAction[] = [];

  actions.push({
    id: '1',
    priority: hasStrategyFinderResults ? 'medium' : 'high',
    category: 'money',
    icon: PhPercent,
    title: hasStrategyFinderResults
      ? 'Update your repayment strategy'
      : 'Find your optimal repayment strategy',
    description: hasStrategyFinderResults
      ? "Life changes. Review your strategy to ensure you're still on the best path to debt freedom."
      : "Based on your employment and loan profile, we can find the best path to debt freedom—whether that's PSLF, IDR, or aggressive payoff.",
    impact: 'Potentially save thousands',
    cta: hasStrategyFinderResults ? 'Review Strategy' : 'Run Strategy Finder',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  });

  if (section127Remaining > 0) {
    actions.push({
      id: '2',
      priority: section127Remaining > 1000 ? 'high' : 'medium',
      category: 'opportunity',
      icon: PhHandshake,
      title: `${formatCurrency(section127Remaining)} of Section 127 unused`,
      description: `You've used ${formatCurrency(section127Used)} of your ${formatCurrency(section127AnnualLimit)} tax-free benefit this year. Allocating the rest to loans could accelerate payoff.`,
      impact: 'Pay off faster',
      cta: 'Learn More',
      route: 'authenticated.repaying-student-debt.repayment-options',
    });
  }

  return actions;
};

interface ActionItemCardsSignature {
  Args: {
    items: ContributionActionItem[];
    person: PersonModel;
    onAddressSubmit: (item: ContributionActionItem, addressId: string) => Promise<void>;
    addressOptions: (item: ContributionActionItem) => Array<{ id: string; label: string }>;
    /** Forwarded to every upload-capable card — see IssueNotificationCard. */
    onUploadComplete?: () => void;
  };
}

/**
 * The server action-item cards, extracted so the unified "Needs Your
 * Attention" section can render at either of its two positions (top of page
 * or directly after the hero) without duplicating this branching.
 */
const ActionItemCards: TOC<ActionItemCardsSignature> = <template>
  {{#each @items as |item|}}
    {{#if item.resolution}}
      <ResolutionAlert @alert={{item.resolution}} @surface="dashboard" />
    {{else if item.tasks}}
      <GroupedIssueNotificationCard
        @item={{item}}
        @person={{@person}}
        @onAddressSubmit={{@onAddressSubmit}}
        @onUploadComplete={{@onUploadComplete}}
      />
    {{else if (eq item.resolutionType "upload")}}
      <IssueNotificationCard
        @title={{item.title}}
        @description={{item.description}}
        @person={{@person}}
        @successMessage={{or
          item.successMessage
          (t "slr.reimagined.successfully_added_loan_account")
        }}
        @onUploadComplete={{@onUploadComplete}}
      />
    {{else if (eq item.resolutionType "contact-support")}}
      <Card @elevation="none" @padding="none" class="px-4 py-6 text-left w-full">
        <div class="tio-row-between gap-4">
          <div class="tio-row">
            <div class="tio-row-center rounded-lg shrink-0 size-12 {{getActionItemIconBg item}}">
              <PhWarningCircle @size={{32}} class={{getActionItemIconColor item}} />
            </div>
            <div class="tio-col-extra-tight">
              <Heading @level={{4}} class="tio-fg-secondary capitalize tracking-tight">
                {{item.title}}
              </Heading>
              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                {{item.description}}
              </Text>
            </div>
          </div>
          <LinkTo
            @route="authenticated.contact-us"
            class="tio-text-sm-medium tio-fg-info hover:tio-fg-info-body underline whitespace-nowrap shrink-0"
            data-test-contact-support-link
          >
            {{t "slr.reimagined.contact_support_link"}}
          </LinkTo>
        </div>
      </Card>
    {{else if (eq item.resolutionType "dropdown")}}
      <AddressSelectionCard
        @title={{item.title}}
        @description={{item.description}}
        @addresses={{@addressOptions item}}
        @onSubmit={{fn @onAddressSubmit item}}
        data-test-slr-dashboard-blank-payment-address-card
      />
    {{else if (eq item.issueType "account-ineligible")}}
      <IssueNotificationCard
        @title={{item.title}}
        @description={{item.description}}
        @person={{@person}}
        @successMessage={{or
          item.successMessage
          (t "slr.reimagined.successfully_added_loan_account")
        }}
        @onUploadComplete={{@onUploadComplete}}
        data-test-slr-dashboard-account-ineligible-card
      />
    {{else}}
      <Card @elevation="none" @padding="none" class="px-4 py-6 text-left w-full">
        <div class="tio-row-between">
          <div class="tio-row">
            <div class="tio-row-center rounded-lg shrink-0 size-12 {{getActionItemIconBg item}}">
              {{#if (eq item.issueType "account-setup")}}
                <PhLinkBreak @size={{32}} class={{getActionItemIconColor item}} />
              {{else if (eq item.issueType "plan-requirement")}}
                <PhShieldSlash @size={{32}} class={{getActionItemIconColor item}} />
              {{else}}
                <PhWarningCircle @size={{32}} class={{getActionItemIconColor item}} />
              {{/if}}
            </div>
            <div class="tio-col-extra-tight">
              <div class="tio-row-tight">
                <Heading @level={{4}} class="tio-fg-secondary capitalize tracking-tight">
                  {{item.title}}
                </Heading>
                {{#if item.badge}}
                  <Chip @intent={{item.badgeIntent}} @size="sm">
                    {{item.badge}}
                  </Chip>
                {{/if}}
              </div>
              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                {{item.description}}
              </Text>
            </div>
          </div>
        </div>
      </Card>
    {{/if}}
  {{/each}}
</template>;

/**
 * Receiving-account status pill for hard-ineligible loan-status verdicts
 * (Ineligible / In default page state, node 1031-2202). Parent-PLUS verdicts
 * are deliberately absent — that page state shows no account pill.
 */
// defaulted/delinquent/in_collections/past_due are exact-match reasons the backend
// preserves old copy for (EmployeePlanAccountGoodStandingEvaluationService); everything
// else — status_not_in_good_standing, the true generic fallback — is named dynamically
// from the plan's configured criteria instead (see `dynamicCriteriaPhrase`).
const INELIGIBLE_STATUS_WORD_KEYS: Record<string, string> = {
  defaulted: 'slr.loan_standing.banner.status_word.in_default',
  delinquent: 'slr.loan_standing.banner.status_word.delinquent',
  in_collections: 'slr.loan_standing.banner.status_word.in_collections',
  past_due: 'slr.loan_standing.banner.status_word.past_due',
  delinquent_or_collections: 'slr.loan_standing.banner.status_word.delinquent_or_collections',
};

export default class ReimaginedContributionHistory extends Component<ReimaginedContributionHistorySignature> {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: Store;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare employee: EmployeeService;
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;

  @tracked isAccountDrawerOpen = false;
  @tracked isPdfDrawerOpen = false;
  @tracked isBenefitDrawerOpen = false;
  @tracked isPaymentHistoryDrawerOpen = false;
  @tracked isUploadDrawerOpen = false;
  @tracked selectedPaymentYear = '';
  @tracked showEditAddress = false;
  @tracked editAddressHasSubmitted = false;
  @tracked selectedAddress?: PostalAddressModel;
  @tracked addressConfirmationChecked = false;

  get history() {
    return this.args.model.contributionHistory;
  }

  get contributions() {
    return this.args.model.employerContributions.contributions;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      this.intl.t('slr.reimagined.your_employer')
    );
  }

  get person() {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person.activeAccounts;
  }

  get accountReceivingContributions() {
    return this.person.accountReceivingContributions;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  get isAddressSubmitDisabled() {
    return (
      !this.selectedAddress || this.editAddressHasSubmitted || !this.addressConfirmationChecked
    );
  }

  get selectedAddressId() {
    return this.selectedAddress?.id || '';
  }

  /**
   * User's first name for greeting
   */
  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName || this.intl.t('slr.reimagined.default_name')
    );
  }

  /**
   * Time-based greeting
   */
  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return this.intl.t('slr.reimagined.good_morning');
    if (hour < 17) return this.intl.t('slr.reimagined.good_afternoon');
    return this.intl.t('slr.reimagined.good_evening');
  }

  /**
   * Projected annual contribution
   */
  get projectedAnnual() {
    return this.contributions.projectedAnnual || 0;
  }

  /**
   * Benefit year to date
   */
  get benefitYearToDate() {
    return this.contributions.yearToDate || 0;
  }

  /** Server-priced monthly contribution in dollars; inline tiers support offline fixtures. */
  get scheduledMonthlyContribution() {
    const fetched = this.history.scheduledMonthlyAmount;
    if (fetched && fetched > 0) return fetched;
    const cents = this.plan?.tiers?.[0]?.amount;
    return cents && cents > 0 ? cents / 100 : 0;
  }

  /**
   * Next contribution amount (dollars). Prefers the live preview amount; a
   * present-but-zero preview row (scheduled but not yet populated) is treated
   * the same as a missing row (`nextPayment` null) — fall back first to the
   * server-priced scheduled amount, then to the run-rate projection. The
   * scheduled amount must win over the projection: when the preview endpoint
   * is down, averaging YTD actuals over elapsed benefit-year months bears no
   * relation to the next payment (one $250 payment over a 10-month-old
   * benefit year rendered "$25" for a $250/month user).
   */
  get nextContribution() {
    const previewed = this.history.nextPayment?.amount;
    return previewed && previewed > 0
      ? previewed
      : this.scheduledMonthlyContribution || this.monthlyContribution;
  }

  /**
   * Monthly contribution (dollars). Use a validation-free next-payment preview
   * or scheduled pricing before estimating a run rate from irregular past payments.
   */
  get monthlyContribution() {
    const previewed = this.history.nextPayment?.amount;
    return (
      (previewed && previewed > 0 && this.history.paymentValidationErrors.length === 0
        ? previewed
        : 0) ||
      this.scheduledMonthlyContribution ||
      Math.round(this.projectedAnnual / 12) ||
      0
    );
  }

  get annualContribution() {
    return this.monthlyContribution * 12;
  }

  // ── Page State ──────────────────────────────────────────────

  get pageState(): SlrPageState | undefined {
    // A payable preview plus completed payments contradicts a waiting-period report.
    if (
      this.history.pageState === 'waiting-period' &&
      this.history.paymentCount > 0 &&
      (this.history.nextPayment?.amount ?? 0) > 0 &&
      this.history.paymentValidationErrors.length === 0
    ) {
      return 'active';
    }
    return this.history.pageState;
  }

  get showFullDashboard() {
    const ps = this.pageState;
    return ps === 'active' || ps === undefined;
  }

  get slotAttentionCount() {
    return this.args.slotAttentionCount ?? 0;
  }

  get visibleActionItems() {
    if (!this.showActionItems) return [];
    return this.actionItems.filter(
      (item) => !item.resolution || !this.memberResolutions.isDismissed(item.resolution)
    );
  }

  // An answered question stays on screen as its confirmation card, but no longer asks anything.
  get openActionItems() {
    return this.visibleActionItems.filter(
      (item) => !item.resolution || !this.memberResolutions.answerFor(item.resolution)
    );
  }

  /**
   * Everything under the single "Needs Your Attention" header: the route's
   * slotted cards plus the open server action items. Zero hides the section
   * entirely.
   */
  get attentionCount() {
    return this.slotAttentionCount + this.openActionItems.length;
  }

  // The section outlives its count: a confirmation card still shows under a "(0)".
  get showsAttentionSection() {
    return this.slotAttentionCount + this.visibleActionItems.length > 0;
  }

  get showLoanAccounts() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  get showContributionHistory() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  get showActionItems() {
    const ps = this.pageState;
    return ps === 'active' || ps === undefined || this.actionItems.length > 0;
  }

  /**
   * The plan's configured LoanStatus/LoanType values behind the account's current
   * ineligibility, humanized and joined — only meaningful for the two reasons whose
   * alert copy names the actual configured criteria rather than fixed text.
   */
  dynamicCriteriaPhrase = trackedFunction(this, async (): Promise<string | null> => {
    const state = this.args.loanStandingState;
    const reason = state?.primaryReason;
    const employeePlanAccountId = state?.employeePlanAccountId;
    if (
      !employeePlanAccountId ||
      (reason !== 'status_not_in_good_standing' && reason !== 'loan_type_not_allowed')
    ) {
      return null;
    }

    const { content } = await this.store.request(
      getEmployeePlanAccountEligibilityCriteriaRequest(employeePlanAccountId)
    );
    const { values, flavor } = unwrapEmployeePlanAccountEligibilityCriteria(
      content as EmployeePlanAccountEligibilityCriteriaResponse
    );
    if (!flavor || values.length === 0) return null;

    const { content: enumContent } = await this.store.request(
      query<EnumModel>('enum', { 'filter[flavor]': flavor })
    );
    const options = (enumContent.data ?? []) as EnumModel[];
    // Admin-authored enum descriptions are untrusted — this renders inside an
    // htmlSafe translation (see the banner template's `<strong>{status}</strong>`),
    // so it must be escaped before it can reach that trusted-HTML context.
    return values.map((dot) => escapeHtml(humanizeEnumDot(options, dot))).join(', ');
  });

  /** Loading/empty fallback for the two dynamic-criteria variants, below. */
  get dynamicCriteriaFallback() {
    return this.intl.t('slr.loan_standing.banner.status_word.not_in_good_standing');
  }

  /**
   * Page-level hard-ineligible banner (Q1, newest page states 670-466 /
   * 1031-2202 / 904-23493 / 904-24267): replaces the attention card for
   * verdicts no re-upload of the SAME loans can change. Amber on the
   * pre-contribution hero variants (empty state / waiting period), pink on
   * the standard dashboard. Carries the situation-change escape hatch —
   * upload IS facilitated wherever a document can re-scope the blocker.
   */
  get ineligibleBanner(): {
    tone: 'danger' | 'warning';
    titleKey: string;
    variant: 'parent_plus' | 'loan_status' | 'loan_type' | 'name_mismatch' | 'federal' | 'private';
    statusWord?: string;
    typesWord?: string;
    accountLabel?: string;
    servicerName?: string;
  } | null {
    const state = this.args.loanStandingState;
    if (state?.status !== 'ineligible') return null;

    const tone =
      this.pageState === 'waiting-period' || this.paymentCount === 0 ? 'warning' : 'danger';
    const titleKey = 'slr.loan_standing.banner.ineligible_title';

    if (state.primaryReason === 'parent_plus_only') {
      return { tone, variant: 'parent_plus', titleKey, servicerName: this.receivingServicerName };
    }

    // A denied name match is terminal-ineligible but is not a loan status.
    if (state.primaryReason === 'name_mismatch') {
      return {
        tone,
        variant: 'name_mismatch',
        titleKey: 'slr.loan_standing.banner.name_mismatch_title',
        accountLabel: this.receivingAccountBannerLabel,
      };
    }

    if (state.primaryReason === 'account_is_federal') {
      return { tone, variant: 'federal', titleKey, accountLabel: this.receivingAccountBannerLabel };
    }

    if (state.primaryReason === 'account_is_private') {
      return { tone, variant: 'private', titleKey, accountLabel: this.receivingAccountBannerLabel };
    }

    if (state.primaryReason === 'loan_type_not_allowed') {
      return {
        tone,
        variant: 'loan_type',
        titleKey,
        accountLabel: this.receivingAccountBannerLabel,
        typesWord: this.dynamicCriteriaPhrase.value ?? this.dynamicCriteriaFallback,
      };
    }

    const staticStatusWordKey =
      state.primaryReason && INELIGIBLE_STATUS_WORD_KEYS[state.primaryReason];
    const statusWord = staticStatusWordKey
      ? this.intl.t(staticStatusWordKey)
      : (this.dynamicCriteriaPhrase.value ?? this.dynamicCriteriaFallback);
    return {
      tone,
      variant: 'loan_status',
      titleKey,
      accountLabel: this.receivingAccountBannerLabel,
      statusWord,
    };
  }

  /** Bare servicer name for copy that reads "your Mohela account" (QA page-states, Figma 904-23494). */
  get receivingServicerName() {
    // Admin-editable free text (FinancialInstitution#name) — escaped before it can
    // reach the banner's htmlSafe translation.
    const name = this.receivingLoans[0]?.financialInstitution?.name;
    return name ? escapeHtml(name) : undefined;
  }

  /** "Mohela ...8164" — the receiving account as the banner names it. */
  get receivingAccountBannerLabel() {
    const account = this.receivingLoans[0];
    if (!account) return undefined;
    // Admin-editable free text (FinancialInstitution#name) — escaped before it can
    // reach the banner's htmlSafe translation; the account-number suffix we append
    // is server-derived, not free text, so it needs no escaping of its own.
    const name = account.financialInstitution?.name;
    const escapedName = name ? escapeHtml(name) : undefined;
    return [escapedName, this.getAccountNumberSuffix(account)].filter(Boolean).join(' ');
  }

  get pageStateInfoBanner(): {
    title: string;
    description: string;
  } | null {
    const ps = this.pageState;
    if (ps === 'all-pif') {
      return {
        title: this.intl.t('slr.reimagined.page_state_all_pif_title'),
        description: this.intl.t('slr.reimagined.page_state_all_pif_desc'),
      };
    }
    if (ps === 'limit-annual') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_annual_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_annual_desc'),
      };
    }
    if (ps === 'limit-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_lifetime_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_lifetime_desc'),
      };
    }
    if (ps === 'limit-annual-and-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_both_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_both_desc'),
      };
    }
    return null;
  }

  get waitingPeriodDate() {
    const opts = {
      month: 'long' as const,
      day: 'numeric' as const,
      year: 'numeric' as const,
    };
    const dateStr = this.sessionContext.currentEmployee?.eligibilityStartDate;
    return dateStr ? this.intl.formatDate(new Date(dateStr), opts) : undefined;
  }

  // ── Smart Actions ───────────────────────────────────────────

  get section127Info() {
    return this.args.model.section127Info;
  }

  get actions() {
    return createProductionActions({
      section127Remaining: this.section127Info.remaining,
      section127Used: this.section127Info.yearToDateUsed,
      section127AnnualLimit: this.section127Info.annualLimit,
      hasStrategyFinderResults: this.args.model.hasStrategyFinderResults,
      formatCurrency: formatCurrency,
    });
  }

  /**
   * Lifetime employer benefit
   */
  get lifetimeBenefit() {
    return this.contributions.lifetime || 0;
  }

  /**
   * Estimated remaining benefit for the year
   */
  get estimatedRemaining() {
    return Math.max(this.projectedAnnual - this.benefitYearToDate, 0);
  }

  /**
   * Benefit year range (start and end)
   */
  get benefitYearRange() {
    return formatBenefitYearRange();
  }

  /**
   * Benefit year progress percentage
   */
  get benefitYearProgressPercent() {
    if (this.projectedAnnual === 0) return 0;
    return Math.round((this.benefitYearToDate / this.projectedAnnual) * 100);
  }

  /**
   * Effective annual/lifetime limits in cents. The most specific limits-status
   * scope (scheme → batch → product → company) wins, falling back to the
   * plan-level column — matching how the benefit-detail drawer and the actual
   * limit enforcement resolve the cap. Reading `plan.*` directly here showed a
   * stale amount for employees whose limit comes from a scope override.
   */
  get effectiveAnnualLimitCents() {
    return effectiveLimitCents(
      this.args.model.limitsStatus,
      'annual_limit',
      this.plan?.annualLimit
    );
  }

  get effectiveLifetimeLimitCents() {
    return effectiveLimitCents(this.args.model.limitsStatus, 'limit', this.plan?.lifetimeLimit);
  }

  /**
   * Hero variant based on the effective limits (limits-status scope overrides,
   * falling back to the plan):
   * 1 = annual limit — tracker + 2 cards
   * 2 = lifetime limit — tracker + 3 cards
   * 3 = no limit — no tracker, 4 equal cards
   * 4 = both limits — tracker (annual) + 3 cards including lifetime
   */
  get heroVariant(): 1 | 2 | 3 | 4 {
    const annualLimit = this.effectiveAnnualLimitCents ?? 0;
    const lifetimeLimit = this.effectiveLifetimeLimitCents ?? 0;
    if (annualLimit > 0 && lifetimeLimit > 0) return 4;
    if (annualLimit > 0) return 1;
    if (lifetimeLimit > 0) return 2;
    return 3;
  }

  /**
   * The applicable limit amount in dollars for the hero tracker.
   * Uses annual limit (variant 1) or lifetime limit (variant 2).
   */
  get heroLimitAmount() {
    const annualLimit = this.effectiveAnnualLimitCents ?? 0;
    const lifetimeLimit = this.effectiveLifetimeLimitCents ?? 0;
    if (annualLimit > 0) return annualLimit / 100;
    if (lifetimeLimit > 0) return lifetimeLimit / 100;
    return 0;
  }

  /**
   * Lifetime limit in dollars (for variant 4 "both limits" — shown on Total Contribution card)
   */
  get lifetimeLimitAmount() {
    return (this.effectiveLifetimeLimitCents ?? 0) / 100;
  }

  /**
   * Annual limit in dollars
   */
  get annualLimitDollars() {
    return (this.effectiveAnnualLimitCents ?? 0) / 100;
  }

  /**
   * Progress percentage for annual usage against annual limit.
   */
  get annualProgressPercent() {
    if (this.annualLimitDollars <= 0) return 0;
    return Math.min(Math.round((this.benefitYearToDate / this.annualLimitDollars) * 100), 100);
  }

  /**
   * Progress percentage for lifetime usage against lifetime limit.
   */
  get lifetimeProgressPercent() {
    if (this.lifetimeLimitAmount <= 0) return 0;
    return Math.min(Math.round((this.lifetimeBenefit / this.lifetimeLimitAmount) * 100), 100);
  }

  /**
   * Only visible receiving-account actions block contributions; paymentValidationErrors mix accounts and plan checks.
   * Limits, PIF and verification have separate page states. An alerts outage, or an alert marked
   * `blocks-contribution: false`, never blocks.
   */
  get isBlocked() {
    return this.openActionItems.some(actionItemBlocksContribution);
  }

  /**
   * Loan-standing verification is actively blocking the next contribution:
   * the receiving account's audit state needs the user's attention
   * (pending upload / try again / ineligible / cannot determine). `processing`
   * and `eligible` never flag the stat — an in-review or verified account
   * must not show a scary strikethrough.
   */
  get nextContributionRequiresVerification() {
    const state = this.args.loanStandingState;
    if (!state) return false;
    // Strike the stat only for overdue or hard-ineligible states; due-soon remains unblocked.
    return !!state.auditPastDue || state.status === 'ineligible';
  }

  /**
   * Label under the struck stat, per the newest page states: "No Eligible
   * Accounts" (parent PLUS, 670-466), "Not Eligible" (loan status,
   * 1031-2202); overdue falls back to "requires verification" in the hero.
   */
  get nextContributionVerificationLabel() {
    const state = this.args.loanStandingState;
    if (state?.status !== 'ineligible') return undefined;
    return state.primaryReason === 'parent_plus_only'
      ? this.intl.t('slr.loan_standing.attention.no_eligible_accounts')
      : this.intl.t('slr.loan_standing.attention.not_eligible');
  }

  /** The receiving account passed verification — show the verified chip. */
  get receivingAccountVerified() {
    return this.args.loanStandingState?.status === 'eligible';
  }

  /**
   * Verification status pill on the CONTRIBUTION-RECEIVING account card only
   * (Figma: Loan Accounts / Receiving Contributions) — "Other Loan Accounts"
   * never carries one, since verification is scoped to the account receiving
   * contributions. Three designed states, each dated:
   *   eligible           → "eligible until {contributionPeriodEnd}"
   *   audit window open  → "verification due {auditRequiredBy}"
   *   deadline passed    → "verification overdue since {auditRequiredBy + 1d}"
   *                        (the first day contributions are blocked)
   * Falls back to the bare "verified" chip when an eligible account has no
   * audit window (no schedule configured ⇒ no "until" date to state).
   */
  /** "January 2027" — the verified user's next verification ask; null unless eligible with a known next audit month. */
  get nextAuditReminderMonth() {
    const state = this.args.loanStandingState;
    if (!state?.featureEnabled || state.status !== 'eligible') return null;
    const parsed = parseLocalishDate(state.nextAuditStartsOn);
    return parsed ? this.intl.formatDate(parsed, { month: 'long', year: 'numeric' }) : null;
  }

  get receivingAccountStatusPill() {
    const state = this.args.loanStandingState;
    if (!state?.featureEnabled) return null;

    const asMonthDay = (date: Date) =>
      this.intl.formatDate(date, { month: '2-digit', day: '2-digit' });
    const requiredBy = parseLocalishDate(state.auditRequiredBy);

    if (state.status === 'eligible') {
      const periodEnd = parseLocalishDate(state.contributionPeriodEnd);
      return periodEnd
        ? {
            kind: 'eligible' as const,
            intent: 'success' as const,
            labelKey: 'slr.loan_standing.account_pill.eligible_until',
            date: asMonthDay(periodEnd),
          }
        : {
            kind: 'verified' as const,
            intent: 'success' as const,
            labelKey: 'slr.loan_standing.attention.verified',
            date: undefined,
          };
    }

    // Hard-ineligible verdicts have no deadline; their banner carries the reason instead of a dated pill.
    if (state.status === 'ineligible') {
      return {
        kind: 'status' as const,
        intent: 'danger' as const,
        labelKey: 'slr.loan_standing.account_pill.ineligible',
        date: undefined,
      };
    }

    if (state.auditPastDue) {
      if (!requiredBy) return null;
      const since = new Date(
        requiredBy.getFullYear(),
        requiredBy.getMonth(),
        requiredBy.getDate() + 1
      );
      return {
        kind: 'overdue' as const,
        intent: 'danger' as const,
        labelKey: 'slr.loan_standing.account_pill.verification_overdue_since',
        date: asMonthDay(since),
      };
    }

    if (requiredBy) {
      return {
        kind: 'due' as const,
        intent: 'warning' as const,
        labelKey: 'slr.loan_standing.account_pill.verification_due',
        date: asMonthDay(requiredBy),
      };
    }

    return null;
  }

  /**
   * Label for the hero tracker limit — "annual limit" or "lifetime limit"
   */
  get heroLimitLabel() {
    const annualLimit = this.effectiveAnnualLimitCents ?? 0;
    if (annualLimit > 0) return this.intl.t('slr.reimagined.annual_limit');
    return this.intl.t('slr.reimagined.lifetime_limit');
  }

  /**
   * Estimated remaining for the hero tracker based on which limit applies.
   */
  get heroEstimatedRemaining() {
    if (this.heroVariant === 1 || this.heroVariant === 4) {
      return Math.max(this.heroLimitAmount - this.benefitYearToDate, 0);
    }
    if (this.heroVariant === 2) {
      return Math.max(this.heroLimitAmount - this.lifetimeBenefit, 0);
    }
    return 0;
  }

  /**
   * Progress percentage for the hero tracker based on which limit applies.
   */
  get heroProgressPercent() {
    if (this.heroLimitAmount <= 0) return 0;
    const used =
      this.heroVariant === 1 || this.heroVariant === 4
        ? this.benefitYearToDate
        : this.lifetimeBenefit;
    return Math.min(Math.round((used / this.heroLimitAmount) * 100), 100);
  }

  /**
   * Formatted last payment date (e.g. "12/2025")
   */
  get lastPaymentFormatted() {
    const payment = this.history.lastPayment;
    if (!payment?.paidDate) return '';
    const date = new Date(payment.paidDate);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get lastPaymentAmount() {
    return this.history.lastPayment?.amount ?? 0;
  }

  /**
   * Current calendar year for the "{year} Contribution" label
   */
  get currentYear() {
    return new Date().getFullYear();
  }

  /**
   * Number of contribution payments made this benefit year
   */
  get paymentCount() {
    const currentYear = String(this.currentYear);
    const yearData = this.paymentsByYear?.find((y) => y.year === currentYear);
    return yearData?.paymentCount ?? 0;
  }

  // ── Stat Card Metrics ───────────────────────────────────────

  get totalBalance() {
    return this.activeAccounts.reduce(
      (sum: number, account: AccountModel) => sum + (account.loanTotal ?? 0),
      0
    );
  }

  get totalMonthlyPayment() {
    return this.activeAccounts.reduce(
      (sum: number, account: AccountModel) => sum + (account.loanMinMonthlyPayment ?? 0),
      0
    );
  }

  get averageInterestRate() {
    const accounts = this.activeAccounts;
    if (!accounts.length) return 0;
    const weighted = accounts.reduce(
      (sum: number, account: AccountModel) =>
        sum + (account.loanTotal ?? 0) * (account.loanInterestAverage ?? 0),
      0
    );
    const total = accounts.reduce(
      (sum: number, account: AccountModel) => sum + (account.loanTotal ?? 0),
      0
    );
    return total > 0 ? Number((weighted / total).toFixed(1)) : 0;
  }

  get yearProgressAmount() {
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return contributions.contributions.yearToDate;
    }
    return 0;
  }

  get nextPaymentSubtext() {
    const accounts = this.activeAccounts;
    const dates = accounts
      .flatMap((a: AccountModel) => a.activeLoans ?? [])
      .map((loan) => loan.observation?.['next-payment-date'])
      .filter((d): d is string => !!d)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    if (!dates[0]) return '';
    const formatted = this.intl.formatDate(new Date(dates[0]), {
      month: 'short',
      day: 'numeric',
    });
    return this.intl.t('slr.reimagined.stat_next_due', { date: formatted });
  }

  /**
   * Formatted date of the earliest contribution for "as of" subtext.
   * Uses the last payment in the history (oldest chronologically).
   */
  get lifetimeAsOfDate() {
    const payments = this.history.payments;
    if (!payments?.length) return '';
    const oldest = payments[payments.length - 1];
    const dateStr = oldest?.paidDate;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ── Milestone Tracker ─────────────────────────────────────

  get totalOriginalBalance() {
    return this.activeAccounts.reduce(
      (sum: number, account: AccountModel) => sum + (account.summary?.cumulativePrincipal ?? 0),
      0
    );
  }

  get progressPercent() {
    return computeProgressPercent(this.totalOriginalBalance, this.totalBalance);
  }

  get milestones() {
    return buildDebtMilestones(this.totalOriginalBalance, this.totalBalance);
  }

  /**
   * Next expected contribution date from the preview API.
   * Falls back to first of next calendar month if no preview data.
   */
  get nextContributionDate() {
    const nextPayment = this.history.nextPayment;
    if (nextPayment?.benefitCycle) {
      const parts = nextPayment.benefitCycle.split('-');
      if (parts.length >= 2) {
        const yearPart = parts[0];
        const monthPart = parts[1];

        if (yearPart && monthPart) {
          const year = parseInt(yearPart, 10);
          const month = parseInt(monthPart, 10);

          if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
            const date = new Date(year, month - 1, 1);
            return this.intl.formatDate(date, { month: '2-digit', year: 'numeric' });
          }
        }
      }
    }
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return this.intl.formatDate(nextMonth, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get addressOptions() {
    return (
      this.addressData.value?.map((address: PostalAddressModel) => ({
        label: htmlSafe(
          address.addressLine1 +
            (address.addressLine2 ? '\n ' + address.addressLine2 : '') +
            (address.addressLine3 ? '\n ' + address.addressLine3 : '')
        ),
        key: address.id,
        address,
      })) || []
    );
  }

  addressData = trackedFunction(this, async () => {
    const { content } = (await this.store.request(
      query<PostalAddressModel>('postal-address', {
        'filter[financialInstitution]':
          this.person.accountReceivingContributions?.financialInstitution?.id ?? '',
        'filter[isActive]': true,
        'filter[type]': 'PAYMENT',
        sort: 'address-line1',
        'page[offset]': 0,
        'page[limit]': 400,
      })
    )) as { content: { data: PostalAddressModel[] } };
    return content.data;
  });

  toggleEditAddress = () => {
    this.showEditAddress = !this.showEditAddress;
    this.selectedAddress = undefined;
    this.addressConfirmationChecked = false;
    this.editAddressHasSubmitted = false;
  };

  setSelectedAddress = (id: string | null) => {
    this.selectedAddress = this.addressOptions.find((a) => a.key === id)?.address;
  };

  updateAddress = () => {
    return this.updateAddressTask.perform();
  };

  updateAddressTask = dropTask(async () => {
    this.editAddressHasSubmitted = true;
    const account = this.person.accountReceivingContributions;
    if (!this.selectedAddress || !account) {
      this.editAddressHasSubmitted = false;
      return;
    }
    try {
      const newAddress = this.addressData.value?.find(
        (a: PostalAddressModel) => a.id === this.selectedAddress?.id
      );
      if (!newAddress) {
        throw new Error('Address not found');
      }
      account.postalAddress = newAddress as PostalAddressModel;
      await saveRecord(this.store, account);
      this.toggleEditAddress();
      this.employee.refreshSlrState();
      this.router.refresh('authenticated.slr.dashboard');
      const successMessage = this.intl.t('common.accounts.update_address.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMessage = this.intl.t('common.accounts.update_address.error');
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      this.editAddressHasSubmitted = false;
    }
  });

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  toggleUploadDrawer = () => {
    this.isUploadDrawerOpen = !this.isUploadDrawerOpen;
  };

  /**
   * A reflected statement upload can change every server-derived answer on
   * this page — /slr-alerts recomputes live per request (Slr::AlertService),
   * so the action items, page state and linked accounts are all stale until
   * something refetches. The route model is the only thing that fetches them,
   * hence the refresh; `employee.slrAlertsData` is a service-level
   * trackedFunction that would otherwise keep the dashboard tile on its
   * pre-upload answer for the rest of the session.
   */
  handleUploadComplete = () => {
    this.employee.refreshSlrState();
    this.router.refresh('authenticated.slr.dashboard');
  };

  togglePdfDrawer = () => {
    this.isPdfDrawerOpen = !this.isPdfDrawerOpen;
  };

  toggleBenefitDrawer = () => {
    this.isBenefitDrawerOpen = !this.isBenefitDrawerOpen;
  };

  openPaymentHistoryDrawer = (year: string) => {
    this.selectedPaymentYear = year;
    this.isPaymentHistoryDrawerOpen = true;
  };

  closePaymentHistoryDrawer = () => {
    this.isPaymentHistoryDrawerOpen = false;
  };

  get paymentsForSelectedYear(): ContributionPaymentRecord[] {
    if (!this.selectedPaymentYear) return [];
    return this.history.payments.filter((payment) => {
      if (payment.isFuture) return false;
      return getPaymentYear(payment) === this.selectedPaymentYear;
    });
  }

  get selectedYearTotal() {
    return this.paymentsForSelectedYear.reduce((sum, p) => sum + p.amount, 0);
  }

  get plan() {
    return this.sessionContext.currentEmployee?.plan ?? null;
  }

  onAccountChanged = () => {
    // /slr-alerts is scoped to the account receiving contributions
    // (Slr::AlertService), so switching accounts changes its answer — the
    // route refresh covers this page, refreshSlrState covers the dashboard
    // tile's own copy. (The live-upload strip retires in the drawer itself,
    // which owns the mutation and serves every route that embeds it.)
    this.employee.refreshSlrState();
    this.router.refresh('authenticated.slr.dashboard');
  };

  get receivingLoans() {
    return this.activeAccounts.filter(
      (account) => account.id === this.accountReceivingContributions?.id
    );
  }

  get otherLoans() {
    return this.activeAccounts.filter(
      (account) => account.id !== this.accountReceivingContributions?.id
    );
  }

  // The account resource carries no contribution totals, so they come from the payment history,
  // matched by the servicer name each payment records.
  #paymentsReceivedBy(account: AccountModel) {
    const name = account.financialInstitution?.name;
    if (!name) return [];
    return (this.history?.payments ?? []).filter(
      (payment) =>
        !payment.isFuture &&
        (payment.status === 'sent' || payment.status === 'resent') &&
        payment.servicer === name
    );
  }

  getAccountContributionAmount = (account: AccountModel) => {
    return this.#paymentsReceivedBy(account).reduce((total, payment) => total + payment.amount, 0);
  };

  getAccountContributionCount = (account: AccountModel) => {
    return this.#paymentsReceivedBy(account).length;
  };

  getAccountBalance = (account: AccountModel) => {
    return account.loanTotal;
  };

  getAccountLoanCount = (account: AccountModel) => {
    return account.summary?.loanCount ?? 0;
  };

  getAccountNumber = (account: AccountModel) => {
    return account.accountNumber || '';
  };

  getAccountNumberSuffix = (account: AccountModel) => {
    const num = account.accountNumber || '';
    return num.length >= 4 ? `...${num.slice(-4)}` : num;
  };

  getAccountLastUpdated = (account: AccountModel) => {
    const dateStr = account.providerSuccessAt;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  getAccountMinMonthlyPayment = (account: AccountModel) => {
    return account.loanMinMonthlyPayment;
  };

  getAccountPaymentAddress = (account: AccountModel) => {
    const address = account.postalAddress;
    if (!address) return '';
    const parts = [address.addressLine1];
    if (address.city || address.state || address.postalCode) {
      parts.push(
        [address.city, address.state].filter(Boolean).join(', ') +
          (address.postalCode ? ' ' + address.postalCode : '')
      );
    }
    return parts.filter(Boolean).join(' ');
  };

  get recentPayments() {
    return this.history.payments.filter((payment) => !payment.isFuture).slice(0, 3);
  }

  get lastPaymentIndex() {
    return this.recentPayments.length - 1;
  }

  getPaymentStatusLabel = (status: ContributionPaymentRecord['status']) => {
    if (status === 'sent') return this.intl.t('slr.reimagined.status_sent');
    if (status === 'in_progress') return this.intl.t('slr.reimagined.status_in_progress');
    if (status === 'resent') return this.intl.t('slr.reimagined.status_resent');
    if (status === 'failed') return this.intl.t('slr.reimagined.status_failed');
    return this.intl.t('slr.reimagined.status_pending');
  };

  getPaymentStatusIntent = (status: ContributionPaymentRecord['status']) => {
    if (status === 'sent') return 'success';
    if (status === 'in_progress') return 'primary';
    if (status === 'resent') return 'primary';
    if (status === 'failed') return 'danger';
    return 'default';
  };

  // Suppress action items owned by the receiving-account verdict so each condition has one message.
  get actionItems() {
    return suppressVerdictOwnedActionItems(this.history.actionItems, this.args.loanStandingState);
  }

  getActionItemIconBg = (item: ContributionActionItem) => getActionItemIconBg(item);

  getActionItemIconColor = (item: ContributionActionItem) => getActionItemIconColor(item);

  getAddressOptions = (item: ContributionActionItem) => {
    const addresses = (item.metadata?.available_addresses ?? []) as Array<{
      id: string;
      label: string;
    }>;
    return addresses;
  };

  handleAddressSubmit = async (_item: ContributionActionItem, addressId: string) => {
    const account = this.person.accountReceivingContributions;
    if (!account) throw new Error('No contribution account found');

    const { content: paContent } = (await this.store.request(
      findRecord<PostalAddressModel>('postal-address', addressId)
    )) as { content: { data: PostalAddressModel } };
    account.postalAddress = paContent.data;
    await saveRecord(this.store, account);
    this.employee.refreshSlrState();
    this.router.refresh('authenticated.slr.dashboard');
    this.notifications.add(this.intl.t('common.accounts.update_address.success'), {
      appearance: 'success',
    });
  };

  // Payment History by Year - provided by service (demo or live)
  get paymentsByYear() {
    return this.history.paymentsByYear;
  }

  <template>
    {{! The hero carries the route title, so it renders in every page state;
        only the metric tiles wait on contributions to report. }}
    <ContributionHero
      @title={{t "dashboard_tiles.student_loan_repayment"}}
      @headingLevel={{1}}
      @showMetrics={{this.showFullDashboard}}
      @lrapEnrolled={{@lrapEnrolled}}
      @greeting={{this.greeting}}
      @firstName={{this.firstName}}
      @variant={{this.heroVariant}}
      @annualUsage={{this.benefitYearToDate}}
      @annualLimit={{this.annualLimitDollars}}
      @annualProgressPercent={{this.annualProgressPercent}}
      @lifetimeUsage={{this.lifetimeBenefit}}
      @lifetimeLimit={{this.lifetimeLimitAmount}}
      @lifetimeProgressPercent={{this.lifetimeProgressPercent}}
      @lifetimeContribution={{this.lifetimeBenefit}}
      @lifetimeAsOfDate={{this.lifetimeAsOfDate}}
      @currentYear={{this.currentYear}}
      @yearContribution={{this.benefitYearToDate}}
      @paymentCount={{this.paymentCount}}
      @lastPaymentAmount={{this.lastPaymentAmount}}
      @lastPaymentDate={{this.lastPaymentFormatted}}
      @nextContribution={{this.nextContribution}}
      @nextContributionDate={{this.nextContributionDate}}
      @blocked={{this.isBlocked}}
      @nextContributionRequiresVerification={{this.nextContributionRequiresVerification}}
      @nextContributionVerificationLabel={{this.nextContributionVerificationLabel}}
      @onViewBenefit={{unless (eq this.pageState "non-slr-never") this.toggleBenefitDrawer}}
    />

    {{#if this.showsAttentionSection}}
      <div data-test-slr-needs-attention>
        <Section>
          <:header>
            {{t "slr.reimagined.needs_your_attention"}}
            <span class="tio-fg-secondary">({{this.attentionCount}})</span>
          </:header>
          <:body>
            <div class="tio-col-tight">
              {{yield to="attention"}}
              {{#if this.visibleActionItems.length}}
                <div data-test-slr-action-items class="tio-col-tight">
                  <ActionItemCards
                    @items={{this.visibleActionItems}}
                    @person={{this.person}}
                    @onAddressSubmit={{this.handleAddressSubmit}}
                    @addressOptions={{this.getAddressOptions}}
                    @onUploadComplete={{this.handleUploadComplete}}
                  />
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      </div>
    {{/if}}

    {{yield to="promo"}}

    {{#if (eq this.pageState "non-slr-never")}}
      <Card data-test-slr-page-state-banner="non-slr-never">
        <Text class="tio-fg-secondary">
          {{t "slr.reimagined.page_state_not_available_desc"}}
        </Text>
      </Card>

    {{else if (eq this.pageState "no-accounts")}}
      <Card data-test-slr-page-state-banner="no-accounts">
        <div class="tio-row-start-between gap-4">
          <div class="tio-col-loose flex-1">
            <Text class="tio-fg-secondary max-w-lg">
              {{t "slr.reimagined.page_state_no_accounts_desc"}}
            </Text>
            <PageStateUploadCta
              @person={{this.person}}
              @onUploadComplete={{this.handleUploadComplete}}
              data-test-slr-no-accounts-upload
            />
          </div>
          <Card @radius="nested" @padding="sm" @elevation="none" class="tio-col w-60 shrink-0">
            <Text @weight="medium" class="tio-fg-secondary">
              {{t "slr.reimagined.page_state_eligible_for"}}
            </Text>
            <div class="flex items-baseline gap-0.5">
              <span class="text-3xl font-semibold tio-fg-primary">
                {{formatCurrency this.monthlyContribution}}
              </span>
              <span class="text-base tio-fg-secondary">{{t
                  "slr.reimagined.page_state_per_month"
                }}</span>
            </div>
            <Text @size="sm" class="tio-fg-muted">
              {{t
                "slr.reimagined.page_state_eligible_annually"
                amount=(formatCurrency this.annualContribution)
              }}
            </Text>
          </Card>
        </div>
      </Card>

    {{else if (eq this.pageState "non-slr-former")}}
      <AlertBanner
        @intent="warning"
        @title={{t "slr.not_eligible.title"}}
        data-test-slr-page-state-banner="non-slr-former"
      >
        <Text @size="sm" @variant="inherit">{{t "slr.not_eligible.message"}}</Text>
      </AlertBanner>

    {{else if (eq this.pageState "waiting-period")}}
      <div data-test-slr-page-state-banner="waiting-period">
        <Section>
          <:header>{{t "slr.reimagined.page_state_waiting_title"}}</:header>
          <:body>
            <div class="tio-row-start-between gap-4">
              <Card @elevation="none" @padding="sm" class="flex-1">
                <Heading @level={{3}} @role="panel-title" class="mb-1">
                  {{t "slr.reimagined.page_state_waiting_eligibility" date=this.waitingPeriodDate}}
                </Heading>
                <Text @size="sm" class="tio-fg-secondary mb-3">
                  {{t "slr.reimagined.page_state_waiting_desc"}}
                </Text>
                <PageStateUploadCta
                  @person={{this.person}}
                  @onUploadComplete={{this.handleUploadComplete}}
                  data-test-slr-waiting-period-upload
                />
              </Card>
              <Card @radius="nested" @padding="sm" @elevation="none" class="tio-col w-60 shrink-0">
                <Text @weight="medium" class="tio-fg-secondary">
                  {{t "slr.reimagined.page_state_soon_eligible"}}
                </Text>
                <div class="flex items-baseline gap-0.5">
                  <span class="text-3xl font-semibold tio-fg-primary">
                    {{formatCurrency this.monthlyContribution}}
                  </span>
                  <span class="text-base tio-fg-secondary">{{t
                      "slr.reimagined.page_state_per_month"
                    }}</span>
                </div>
                <Text @size="sm" class="tio-fg-muted">
                  {{t "slr.reimagined.page_state_starting_date" date=this.waitingPeriodDate}}
                </Text>
              </Card>
            </div>
          </:body>
        </Section>
      </div>
    {{/if}}

    {{#if this.pageStateInfoBanner}}
      <AlertBanner
        @intent="warning"
        @title={{this.pageStateInfoBanner.title}}
        data-test-slr-page-state-info-banner
      >
        <Text @size="sm" @variant="inherit">
          {{this.pageStateInfoBanner.description}}
        </Text>
      </AlertBanner>
    {{/if}}

    {{#if this.ineligibleBanner}}
      {{#let this.ineligibleBanner as |banner|}}
        <AlertBanner
          @intent={{banner.tone}}
          @title={{t banner.titleKey}}
          data-test-slr-ineligible-banner={{banner.variant}}
        >
          <Text @size="sm" @variant="inherit">
            {{#if (eq banner.variant "parent_plus")}}
              {{#if this.ineligibleBanner.servicerName}}
                {{t
                  "slr.loan_standing.banner.no_eligible_accounts_body_with_account"
                  account=this.ineligibleBanner.servicerName
                  htmlSafe=true
                }}
              {{else}}
                {{t "slr.loan_standing.banner.no_eligible_accounts_body" htmlSafe=true}}
              {{/if}}
            {{else if (eq banner.variant "name_mismatch")}}
              {{t
                "slr.loan_standing.banner.name_mismatch_body"
                htmlSafe=true
                account=banner.accountLabel
              }}
            {{else if (eq banner.variant "loan_type")}}
              {{t
                "slr.loan_standing.banner.loan_type_body"
                htmlSafe=true
                account=banner.accountLabel
                types=banner.typesWord
              }}
            {{else if (eq banner.variant "federal")}}
              {{t
                "slr.loan_standing.banner.account_is_federal_body"
                htmlSafe=true
                account=banner.accountLabel
              }}
            {{else if (eq banner.variant "private")}}
              {{t
                "slr.loan_standing.banner.account_is_private_body"
                htmlSafe=true
                account=banner.accountLabel
              }}
            {{else}}
              {{t
                "slr.loan_standing.banner.loan_status_body"
                htmlSafe=true
                account=banner.accountLabel
                status=banner.statusWord
              }}
            {{/if}}
            {{t "slr.loan_standing.banner.escape_hatch_prefix"}}
            {{! Opens the dashboard's own upload drawer instead of leaving
                    for the observability upload page (QA 8/1: the user should
                    stay within the SLR dash). A native inline button, not the
                    shared <Button>: it sits mid-sentence and must flow as
                    link-styled text. }}
            <button
              type="button"
              class="font-semibold underline"
              data-test-slr-ineligible-banner-upload-link
              {{on "click" this.toggleUploadDrawer}}
            >
              {{t "slr.loan_standing.banner.escape_hatch_link"}}</button>.
          </Text>
        </AlertBanner>
      {{/let}}
    {{/if}}

    {{#if this.showLoanAccounts}}
      <div data-test-slr-loan-accounts>
        <Section>
          <:header>
            <span class="tio-row-tight">
              <PhBank class="tio-icon-sm tio-fg-secondary" />
              {{t "slr.reimagined.loan_accounts"}}
            </span>
          </:header>
          <:actions>
            <Button
              @intent="primary"
              @appearance="outlined"
              @size="sm"
              {{on "click" this.toggleUploadDrawer}}
            >
              {{t "slr.reimagined.update_loan_information"}}
            </Button>
          </:actions>
          <:body>
            <div class="tio-col-flush gap-8">
              {{#if this.receivingLoans.length}}
                <div class="tio-col-loose">
                  <div class="tio-row-tight text-base">
                    <Heading @level={{3}}>
                      {{t "slr.reimagined.receiving_contributions"}}
                    </Heading>
                    <span class="tio-fg-disabled font-medium">|</span>
                    <button
                      type="button"
                      aria-label={{t "slr.reimagined.change_receiving_account"}}
                      class="tio-fg-info font-medium underline decoration-solid hover:tio-fg-info-body cursor-pointer"
                      {{on "click" this.toggleAccountDrawer}}
                    >
                      {{t "slr.reimagined.change"}}
                    </button>
                  </div>

                  <div class="tio-col">
                    {{#each this.receivingLoans as |account|}}
                      <Card @elevation="none" @padding="sm">
                        <div class="tio-row-between">
                          <div class="tio-col-flush gap-1.5">
                            <div class="tio-row-tight">
                              <Heading @level={{4}}>
                                {{account.financialInstitution.name}}
                                {{#if (this.getAccountNumber account)}}
                                  <span class="tio-fg-muted font-normal">
                                    {{this.getAccountNumberSuffix account}}
                                  </span>
                                {{/if}}
                              </Heading>
                              <Chip @intent="success" @size="sm">
                                {{t "slr.reimagined.active"}}
                              </Chip>
                              {{#if this.receivingAccountStatusPill}}
                                {{! Receiving account ONLY — the Other Loan
                                  Accounts list below never renders this. }}
                                <Chip
                                  @intent={{this.receivingAccountStatusPill.intent}}
                                  @appearance="faded"
                                  @size="sm"
                                  class={{if
                                    (eq this.receivingAccountStatusPill.kind "status")
                                    "lowercase"
                                    "uppercase"
                                  }}
                                  data-test-slr-receiving-account-verified-chip
                                >
                                  {{#if (eq this.receivingAccountStatusPill.kind "due")}}
                                    <PhWarningCircle @size={{12}} />
                                  {{else if (eq this.receivingAccountStatusPill.kind "overdue")}}
                                    <PhProhibit @size={{12}} />
                                  {{else}}
                                    <PhCheckCircle @size={{12}} />
                                  {{/if}}
                                  {{t
                                    this.receivingAccountStatusPill.labelKey
                                    date=this.receivingAccountStatusPill.date
                                  }}
                                </Chip>
                              {{/if}}
                            </div>
                            {{#if this.nextAuditReminderMonth}}
                              {{! Persistent Gtg reminder (QA page-states, Figma 81-670):
                                a returning verified user still sees when the next
                                verification ask comes. }}
                              <Text @size="sm" @variant="muted" data-test-slr-next-audit-reminder>
                                {{t
                                  "slr.loan_standing.account_pill.next_audit_reminder"
                                  month=this.nextAuditReminderMonth
                                }}
                              </Text>
                            {{/if}}
                            <div class="tio-row-snug">
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{formatCurrency (this.getAccountContributionAmount account)}}
                                {{t "slr.reimagined.received"}}
                              </Text>
                              <div class="w-1 h-1 rounded-full tio-bg-neutral"></div>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{this.getAccountContributionCount account}}
                                {{t
                                  "slr.reimagined.contribution_count"
                                  count=(this.getAccountContributionCount account)
                                }}
                              </Text>
                            </div>
                          </div>
                          <div class="tio-stack-end gap-1">
                            <Text @weight="semibold">
                              {{formatCurrency (this.getAccountBalance account)}}
                            </Text>
                            {{#if (this.getAccountLastUpdated account)}}
                              <Text @size="xs" class="tio-fg-disabled">
                                {{t
                                  "slr.reimagined.as_of_date"
                                  date=(this.getAccountLastUpdated account)
                                }}
                              </Text>
                            {{/if}}
                          </div>
                        </div>
                      </Card>
                    {{/each}}
                  </div>
                </div>
              {{/if}}

              {{#if this.otherLoans.length}}
                <div class="tio-col-loose">
                  <Heading @level={{3}}>
                    {{t "slr.reimagined.other_loan_accounts"}}
                  </Heading>

                  <div class="tio-col">
                    {{#each this.otherLoans as |account|}}
                      <Card @elevation="none" @padding="sm" class="tio-row-between">
                        <div class="tio-col-flush gap-1.5">
                          <div class="tio-row-tight">
                            <Heading @level={{4}}>
                              {{account.financialInstitution.name}}
                              <span class="tio-fg-muted font-normal">
                                {{this.getAccountNumberSuffix account}}
                              </span>
                            </Heading>
                          </div>
                          <div class="tio-row-snug">
                            <Text @size="sm" @weight="medium" class="tio-fg-muted">
                              {{formatCurrency (this.getAccountContributionAmount account)}}
                              {{t "slr.reimagined.received"}}
                            </Text>
                            <div class="w-1 h-1 rounded-full tio-bg-neutral"></div>
                            <Text @size="sm" @weight="medium" class="tio-fg-muted">
                              {{#if (this.getAccountContributionCount account)}}
                                {{this.getAccountContributionCount account}}
                                {{t
                                  "slr.reimagined.contribution_count"
                                  count=(this.getAccountContributionCount account)
                                }}
                              {{else}}
                                {{t "slr.reimagined.no_contributions"}}
                              {{/if}}
                            </Text>
                          </div>
                        </div>
                        <div class="tio-stack-end gap-1">
                          <Text @weight="semibold">
                            {{formatCurrency (this.getAccountBalance account)}}
                          </Text>
                          {{#if (this.getAccountLastUpdated account)}}
                            <Text @size="xs" class="tio-fg-disabled">
                              {{t
                                "slr.reimagined.as_of_date"
                                date=(this.getAccountLastUpdated account)
                              }}
                            </Text>
                          {{/if}}
                        </div>
                      </Card>
                    {{/each}}
                  </div>
                </div>
              {{/if}}

              {{#unless (or this.receivingLoans.length this.otherLoans.length)}}
                <div class="py-10 tio-place-center gap-3 text-center">
                  <Text @size="sm" class="tio-fg-muted">
                    {{t "slr.reimagined.no_loan_accounts"}}
                  </Text>
                </div>
              {{/unless}}
            </div>
          </:body>
        </Section>
      </div>
    {{/if}}

    {{#if this.showContributionHistory}}
      <div data-test-slr-payment-history>
        <Section>
          <:header>
            <span class="tio-row-tight">
              <PhMoneyWavy class="tio-icon-sm tio-fg-secondary" />
              {{t "slr.table.reimagined.payment_history"}}
            </span>
          </:header>
          <:actions>
            <Button
              @intent="primary"
              @appearance="outlined"
              @size="sm"
              @icon={{PhDownloadSimple}}
              data-test-slr-download-report-button
              {{on "click" this.togglePdfDrawer}}
            >
              {{t "slr.table.reimagined.download_contribution_report"}}
            </Button>
          </:actions>
          <:body>
            {{#if this.paymentsByYear.length}}
              <div class="app-card-grid app-card-grid--medium">
                {{#each this.paymentsByYear as |yearData|}}
                  <button
                    type="button"
                    data-test-slr-year-card
                    class="tio-card tio-card--radius-card tio-card--elevation-card tio-card--padding-sm tio-elevation-card-hover tio-flex-between flex-col gap-8 text-left cursor-pointer hover:tio-border-info transition-all"
                    {{on "click" (fn this.openPaymentHistoryDrawer yearData.year)}}
                    aria-label={{t "slr.reimagined.view_payment_history" year=yearData.year}}
                  >
                    <div class="tio-col-loose">
                      <Text @weight="semibold" class="tio-fg-muted">
                        {{t "slr.reimagined.year_contribution" year=yearData.year}}
                      </Text>
                      <Heading @level={{3}} class="leading-none">
                        {{formatCurrency yearData.total}}
                      </Heading>
                    </div>

                    <div class="tio-col-loose">
                      <div class="tio-row-tight">
                        <Text @size="sm" @weight="medium" class="tio-fg-muted">{{t
                            "slr.reimagined.account_label"
                            count=yearData.accounts.length
                          }}</Text>
                        <div class="tio-row-snug flex-wrap">
                          {{#each yearData.accounts as |account index|}}
                            {{#if (eq index 0)}}
                              <div class="tio-row-snug">
                                <PhBank @size={{16}} class="tio-fg-secondary" />
                                <Text @size="sm" @weight="semibold" class="tio-fg-secondary">
                                  {{account.name}}
                                </Text>
                              </div>
                            {{else}}
                              <Text @size="sm" @weight="semibold" class="tio-fg-secondary">,</Text>
                              <div class="tio-row-snug">
                                <PhBank @size={{16}} class="tio-fg-secondary" />
                                <Text @size="sm" @weight="semibold" class="tio-fg-secondary">
                                  {{account.name}}
                                </Text>
                              </div>
                            {{/if}}
                          {{/each}}
                        </div>
                      </div>

                      <div class="tio-row-loose pt-2 border-t tio-border-default text-xs">
                        <div data-test-slr-year-sent-count class="tio-row-tight">
                          <PhMoneyWavy @size={{14}} class="tio-fg-info" />
                          <Text @weight="medium">
                            {{t "slr.reimagined.sent_count" count=yearData.sentCount}}
                          </Text>
                        </div>

                        <div
                          data-test-slr-year-returned-count
                          class="tio-row-extra-tight {{unless yearData.returnedCount 'hidden'}}"
                          aria-hidden={{unless yearData.returnedCount "true"}}
                        >
                          <PhWarningCircle @size={{14}} class="tio-fg-primary" />
                          <Text @weight="medium">
                            {{t "slr.reimagined.returned_count" count=yearData.returnedCount}}
                          </Text>
                        </div>

                        <div
                          data-test-slr-year-resent-count
                          class="tio-row-extra-tight {{unless yearData.resentCount 'hidden'}}"
                          aria-hidden={{unless yearData.resentCount "true"}}
                        >
                          <PhArrowCounterClockwise @size={{14}} class="tio-fg-primary" />
                          <Text @weight="medium">
                            {{t "slr.reimagined.resent_count" count=yearData.resentCount}}
                          </Text>
                        </div>

                      </div>
                    </div>
                  </button>
                {{/each}}
              </div>
            {{else}}
              <div class="py-10 tio-place-center gap-2 text-center">
                <Text @size="sm" class="tio-fg-muted">{{t
                    "slr.reimagined.no_payment_history"
                  }}</Text>
              </div>
            {{/if}}
          </:body>
        </Section>
      </div>
    {{/if}}

    <AccountSelectorDrawer
      @isOpen={{this.isAccountDrawerOpen}}
      @onClose={{this.toggleAccountDrawer}}
      @accounts={{this.activeAccounts}}
      @currentAccount={{this.accountReceivingContributions}}
      @personId={{this.person.id}}
      @onAccountChanged={{this.onAccountChanged}}
    />

    <BenefitDetailDrawer
      @isOpen={{this.isBenefitDrawerOpen}}
      @onClose={{this.toggleBenefitDrawer}}
      @plan={{this.plan}}
      @limitsStatus={{@model.limitsStatus}}
    />

    <PdfExportDrawer
      @isOpen={{this.isPdfDrawerOpen}}
      @onClose={{this.togglePdfDrawer}}
      @employeeId={{this.employeeId}}
    />

    <PaymentHistoryDrawer
      @isOpen={{this.isPaymentHistoryDrawerOpen}}
      @onClose={{this.closePaymentHistoryDrawer}}
      @year={{this.selectedPaymentYear}}
      @payments={{this.paymentsForSelectedYear}}
      @total={{this.selectedYearTotal}}
    />

    <DocumentUploadDrawer
      @isOpen={{this.isUploadDrawerOpen}}
      @onClose={{this.toggleUploadDrawer}}
      @onUploadComplete={{this.handleUploadComplete}}
      @person={{this.person}}
      data-test-slr-upload-drawer
    />

    <Modal @isOpen={{this.showEditAddress}} @onClose={{this.toggleEditAddress}} @size="xl">
      <:header>{{t "common.accounts.select_payment_address"}}</:header>
      <:body>
        <VStack class="p-0!">
          <p>{{t "slr.select_address_body_1"}}</p>
          <p class="italic tio-fg-muted">{{t "slr.select_address_body_2"}}</p>
          <form class="tio-col-loose">
            <ReSelect
              @searchable={{true}}
              @clearable={{true}}
              @items={{this.addressOptions}}
              @selected={{this.selectedAddressId}}
              @onChange={{this.setSelectedAddress}}
              @label={{t "slr.reimagined.servicer_address"}}
            />
            <Checkbox
              @isDisabled={{this.updateAddressTask.isRunning}}
              @label={{t "slr.reimagined.address_confirmation"}}
              @checked={{this.addressConfirmationChecked}}
              @onChange={{fn (mut this.addressConfirmationChecked)}}
            />
          </form>
        </VStack>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.updateAddressTask.isRunning}}
          @onCancel={{this.toggleEditAddress}}
          @onSubmit={{this.updateAddress}}
          @submitDisabled={{this.isAddressSubmitDisabled}}
        />
      </:footer>
    </Modal>
  </template>
}
